// AppCard — card de app reutilizado em Apps.tsx + Home.tsx + Customer.tsx.
//
// 2026-05-18: extraído pra Workspace de Clientes. Acepta `customer` opcional
// que adiciona border-left colorida + chip do customer no canto.

import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import type { AppDTO, CustomerDTO } from '../lib/api';

interface Props {
  app: AppDTO & { formsCount?: number };
  customer?: CustomerDTO | null;
  /** Quando true, esconde o chip do customer (usado dentro da página do customer). */
  hideCustomerChip?: boolean;
  /** Quando true, link aponta pra /customers/:slug/apps/:id em vez de /apps/:id. */
  withCustomerContext?: boolean;
  /** Ações no canto superior direito do card (overlay on hover). */
  actions?: React.ReactNode;
}

const DEFAULT_BORDER = '#e2e8f0';

export function AppCard({ app, customer, hideCustomerChip, withCustomerContext, actions }: Props) {
  const icon = (app.config as { icon?: string })?.icon ?? '📦';
  const borderColor = customer?.color || DEFAULT_BORDER;
  const linkTo = withCustomerContext && customer
    ? `/customers/${customer.slug}/apps/${app.id}`
    : `/apps/${app.id}`;

  return (
    <Link
      to={linkTo}
      className="bg-white rounded-xl border border-gray-200 p-5 hover:border-indigo-300 hover:shadow-sm transition-all group block cursor-pointer relative"
      style={{ borderLeft: `4px solid ${borderColor}` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-lg">
          {icon}
        </div>
        {actions && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {actions}
          </div>
        )}
      </div>
      <h3 className="font-semibold text-gray-900 mb-1 truncate group-hover:text-indigo-600">
        {app.name}
      </h3>
      {app.description && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-3">{app.description}</p>
      )}
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500 pt-3 border-t border-gray-100">
        <span className="inline-flex items-center gap-1">
          <FileText size={12} /> {app.formsCount ?? 0} {(app.formsCount ?? 0) === 1 ? 'formulário' : 'formulários'}
        </span>
        {!hideCustomerChip && customer && (
          <CustomerChip customer={customer} compact />
        )}
        {!hideCustomerChip && !customer && (
          <span className="text-[10px] text-gray-400 italic">sem cliente</span>
        )}
      </div>
    </Link>
  );
}

// ─── CustomerChip — pequena badge clicável com avatar + nome ──────
// Usado no AppCard + listas de Forms/Collections (badge dos customers que usam)

interface ChipProps {
  customer: Pick<CustomerDTO, 'slug' | 'icon' | 'color' | 'name'>;
  compact?: boolean;
}

export function CustomerChip({ customer, compact }: ChipProps) {
  const bg = customer.color ? customer.color + '15' : '#f3f4f6';
  const border = customer.color || '#d1d5db';
  return (
    <Link
      to={`/customers/${customer.slug}`}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 ${compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} rounded font-medium hover:opacity-80 transition-opacity`}
      style={{
        background: bg,
        borderLeft: `2px solid ${border}`,
        color: '#0f172a',
      }}
      title={`Cliente: ${customer.name}`}
    >
      <span>{customer.icon ?? '👤'}</span>
      <span className="truncate max-w-[10rem]">{customer.name}</span>
    </Link>
  );
}
